import { Icon, Modal, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ModalValidationErrors } from '@hero/components/molecules';
import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { CONTACT_LIMIT_LIST, DEFAULT_CONTACT_LIMIT_LIST, VALIDATION_ERRORS } from '@hero/constants';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import { useRecogRecipientsBulkUpsert } from '@hero/services/order-service/recognition/hooks/mutations';
import { useNotification } from '@hero/store/hooks';

import { DoubleContactTable } from '../../Contacts';

import * as S from './style';

export function SelectContactModal({
  isOpen,
  onRequestClose,
  selectedContactIds,
  disabledColumns,
  recognitionCode,
  currentRecipientIds
}) {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });
  const { addModalError } = useValidationErrorsStore();
  const pageLayoutContentRef = useRef(null);

  const [defaultAddedRows, setDefaultAddedRows] = useState(
    selectedContactIds?.reduce((result, id) => {
      result[id] = true;
      return result;
    }, {})
  );

  const contactListLimit = useMemo(() => {
    return merchantData?.configs[CONTACT_LIMIT_LIST] ?? DEFAULT_CONTACT_LIMIT_LIST;
  }, [merchantData]);

  const checkCustomErrors = async () => {
    let hasCustomError = false;
    if (Object.keys(defaultAddedRows ?? {}).length === 0) {
      await addModalError(
        <Trans
          i18nKey={VALIDATION_ERRORS.noEnteredInviteEmail}
          components={{ strong: <strong /> }}
        />
      );
      hasCustomError = true;
    }
    return hasCustomError;
  };

  const { mutate: bulkUpsertRecipients } = useRecogRecipientsBulkUpsert();

  const primaryButtonClickHandler = async () => {
    const hasCustomError = await checkCustomErrors();
    if (!hasCustomError) {
      try {
        // Get the selected contact IDs from the DoubleContactTable
        const selectedContacts = Object.keys(defaultAddedRows ?? {});

        // Perform bulk upsert operation
        await bulkUpsertRecipients({
          params: { code: recognitionCode },
          contactIds: selectedContacts,
          recipientContactIds: currentRecipientIds
        });

        onRequestClose();
      } catch (error) {
        showNotification({
          message: t('errorUpdatingRecipients'),
          type: 'error'
        });
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={t('selectRecipientsFromContact')}
      size="xl"
      primaryButtonProps={{
        children: t('save'),
        onClick: primaryButtonClickHandler
      }}
      secondaryButtonProps={{
        children: t('cancel')
      }}
    >
      <S.Wrapper>
        <ModalValidationErrors pageLayoutContentRef={pageLayoutContentRef} />
        <Typography variant="body1Bold">{t('addRecipientEmptyTable')}</Typography>
        <DoubleContactTable
          isModal
          featureType="recognition"
          selectedRows={defaultAddedRows}
          disabledColumns={disabledColumns}
          onChangeSelectedRows={selectedRows => {
            if (Object.keys(selectedRows ?? {})?.length > contactListLimit) {
              showNotification({
                message: t('recipientLimitMessage', {
                  recipientLimit: contactListLimit
                }),
                title: t('recipientLimitTitle'),
                notificationImage: (
                  <Icon name="error" size="xxl" color="var(--color-error-default)" />
                ),
                primaryButtonProps: {
                  style: {
                    margin: '0 auto'
                  }
                }
              });

              return;
            }

            setDefaultAddedRows(selectedRows);
          }}
        />
      </S.Wrapper>
    </Modal>
  );
}

SelectContactModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  selectedContactIds: PropTypes.array,
  disabledColumns: PropTypes.array,
  recognitionCode: PropTypes.string.isRequired,
  currentRecipientIds: PropTypes.array
};
